import { useState, useEffect, useCallback } from "react";
import { api } from "@/services/api";

interface Reminder {
  id: number;
  title: string;
  note: string | null;
  lead_name: string | null;
  owner_name: string | null;
  remind_at: string;
  status: string;
}

const STATUS_OPTIONS = ["pending", "done", "dismissed"];

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-accent-100 text-accent-900",
  done: "bg-primary-600 text-background-50",
  dismissed: "bg-background-200 text-foreground-700",
};

function fmtDate(v: string | null): string {
  if (!v) return "—";
  const t = new Date(v.endsWith("Z") || v.includes("+") ? v : v + "Z").getTime();
  if (Number.isNaN(t)) return "—";
  return new Date(t).toLocaleString();
}

export default function AdminReminders() {
  const [data, setData] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    try {
      const res = await api.get<Reminder[]>(`/reminders?${params.toString()}`);
      setData(res || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load reminders");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  async function updateStatus(id: number, next: string) {
    try {
      await api.patch(`/reminders/${id}/status`, { status: next });
      load();
    } catch (e: any) {
      setError(e?.message || "Update failed");
    }
  }

  const pendingCount = data.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="font-heading text-xl text-foreground-950">Reminders</h2>
          <p className="text-sm text-foreground-600">Follow-ups, calls and deadlines for your team.</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700"
        >
          <i className="ri-add-line"></i> Add Reminder
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-2xl bg-background-100 border border-background-200 p-4">
          <p className="text-[11px] uppercase tracking-wide text-foreground-600">Pending</p>
          <p className="font-heading text-2xl text-foreground-950 mt-1">{pendingCount}</p>
        </div>
        <div className="rounded-2xl bg-background-100 border border-background-200 p-4">
          <p className="text-[11px] uppercase tracking-wide text-foreground-600">Done</p>
          <p className="font-heading text-2xl text-foreground-950 mt-1">{data.filter((r) => r.status === "done").length}</p>
        </div>
        <div className="rounded-2xl bg-background-100 border border-background-200 p-4">
          <p className="text-[11px] uppercase tracking-wide text-foreground-600">Total</p>
          <p className="font-heading text-2xl text-foreground-950 mt-1">{data.length}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); }}
          className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">All Status</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {status && (
          <button onClick={() => { setStatus(""); }} className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-700">
            <i className="ri-close-circle-line"></i> Clear
          </button>
        )}
      </div>

      {error && <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">{error}</div>}

      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left text-xs uppercase tracking-wider text-foreground-600">
                <th className="px-4 py-3">Reminder</th>
                <th className="px-4 py-3">Lead</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {loading && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-foreground-600">
                  <i className="ri-loader-4-line animate-spin"></i> Loading...
                </td></tr>
              )}
              {!loading && data.map((r) => (
                <tr key={r.id} className="hover:bg-background-100/60">
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <p className={`font-semibold text-foreground-950 ${r.status === "done" ? "line-through opacity-70" : ""}`}>{r.title}</p>
                      {r.note && <p className="text-xs text-foreground-600 max-w-[220px] truncate">{r.note}</p>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground-700">{r.lead_name || "—"}</td>
                  <td className="px-4 py-3 text-foreground-700">{fmtDate(r.remind_at)}</td>
                  <td className="px-4 py-3 text-foreground-700">{r.owner_name || "Unassigned"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${STATUS_BADGE[r.status] || "bg-background-200 text-foreground-700"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {r.status !== "done" && (
                        <button
                          onClick={() => updateStatus(r.id, "done")}
                          className="flex items-center justify-center w-8 h-8 rounded-lg text-primary-700 hover:bg-primary-100"
                          aria-label="Mark done"
                          title="Mark done"
                        >
                          <i className="ri-check-line"></i>
                        </button>
                      )}
                      {r.status === "pending" && (
                        <button
                          onClick={() => updateStatus(r.id, "dismissed")}
                          className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100"
                          aria-label="Dismiss"
                          title="Dismiss"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && data.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-foreground-600">No reminders found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {createOpen && <CreateReminder onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
    </div>
  );
}

function CreateReminder({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [leads, setLeads] = useState<any[]>([]);
  const [form, setForm] = useState({ title: "", remind_at: "", note: "", lead_id: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get<any>(`/leads?limit=200`).then((res: any) => setLeads(res?.data ?? [])).catch(() => setLeads([]));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/reminders", {
        title: form.title,
        remind_at: new Date(form.remind_at).toISOString(),
        note: form.note || null,
        lead_id: form.lead_id ? Number(form.lead_id) : null,
      });
      onCreated();
    } catch (err: any) {
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-3xl bg-background-50 border border-background-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100">
          <h3 className="font-heading text-lg text-foreground-950">Add Reminder</h3>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Close">
            <i className="ri-close-line"></i>
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">{error}</div>}
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Title *</label>
            <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Call back NRI owner" className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Due *</label>
            <input required type="datetime-local" value={form.remind_at} onChange={(e) => setForm((f) => ({ ...f, remind_at: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Linked Lead</label>
            <select value={form.lead_id} onChange={(e) => setForm((f) => ({ ...f, lead_id: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
              <option value="">No lead</option>
              {leads.map((l) => <option key={l.id} value={l.id}>{l.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Notes</label>
            <textarea rows={2} maxLength={500} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-5 py-2.5 hover:bg-background-100">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700 disabled:opacity-60">
              {saving ? <><i className="ri-loader-4-line animate-spin"></i> Saving...</> : "Create Reminder"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}